import { Debouncer } from "@tanstack/react-pacer";
import { type LegendListRef } from "@legendapp/list/react";
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { type ThreadId } from "@cmmd-forge/contracts";

export function useChatScrollControls(input: {
  readonly activeThreadId: ThreadId | null;
  readonly terminalOpen: boolean;
  readonly focusComposer: () => void;
  /** Expands the Resting Composer, because arriving at the end returns it. */
  readonly onTimelineReachedEnd: () => void;
  readonly onThreadReset: () => void;
}): {
  readonly legendListRef: RefObject<LegendListRef | null>;
  readonly isAtEndRef: RefObject<boolean>;
  readonly showScrollDebouncer: RefObject<Debouncer<() => void>>;
  readonly showScrollToBottom: boolean;
  readonly setShowScrollToBottom: Dispatch<SetStateAction<boolean>>;
  readonly scrollToEnd: (animated?: boolean) => void;
  readonly onIsAtEndChange: (isAtEnd: boolean) => void;
  readonly getTimelineScrollNode: () => HTMLElement | null;
  readonly isTimelineAtEnd: () => boolean;
} {
  const { activeThreadId, focusComposer, onThreadReset, onTimelineReachedEnd, terminalOpen } =
    input;
  const legendListRef = useRef<LegendListRef | null>(null);
  const isAtEndRef = useRef(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const scrollToEnd = useCallback((animated = false) => {
    legendListRef.current?.scrollToEnd?.({ animated });
  }, []);

  // The Composer's resting gesture needs the element the wheel lands on, and
  // the list is the only thing that knows which of its boxes actually scrolls.
  // Resolved per call rather than captured: the timeline remounts on a thread
  // change, so any node held from before that would be detached.
  const getTimelineScrollNode = useCallback(
    () => legendListRef.current?.getScrollableNode?.() ?? null,
    [],
  );
  const isTimelineAtEnd = useCallback(() => isAtEndRef.current, []);

  const showScrollDebouncer = useRef(
    new Debouncer(
      () => {
        if (!isAtEndRef.current) setShowScrollToBottom(true);
      },
      { wait: 150 },
    ),
  );

  const onIsAtEndChange = useCallback(
    (isAtEnd: boolean) => {
      if (isAtEndRef.current === isAtEnd) return;
      isAtEndRef.current = isAtEnd;
      if (isAtEnd) {
        showScrollDebouncer.current.cancel();
        setShowScrollToBottom(false);
        // Every way back to the newest turn ends here, including the scroll-to-
        // bottom pill and the End key, neither of which produces a wheel event
        // for the Composer's own listener to read.
        onTimelineReachedEnd();
      } else {
        showScrollDebouncer.current.maybeExecute();
      }
    },
    [onTimelineReachedEnd],
  );

  useEffect(() => {
    isAtEndRef.current = true;
    showScrollDebouncer.current.cancel();
    setShowScrollToBottom(false);
    onThreadReset();
  }, [activeThreadId, onThreadReset]);

  useEffect(() => {
    if (!activeThreadId || terminalOpen) return;
    const frame = window.requestAnimationFrame(() => {
      focusComposer();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [activeThreadId, focusComposer, terminalOpen]);

  return {
    legendListRef,
    isAtEndRef,
    showScrollDebouncer,
    showScrollToBottom,
    setShowScrollToBottom,
    scrollToEnd,
    onIsAtEndChange,
    getTimelineScrollNode,
    isTimelineAtEnd,
  };
}
